const rules = {
  required: value => !!value || 'El campo es obligatorio.',

  requiredArray: value =>
    (!!value && value.length > 0) || 'El campo es obligatorio',

  email: value => {
    const pattern =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(value) || 'Correo electrónico inválido.';
  },

  numerico: value => {
    const pattern = /^[0-9]+$/;
    return pattern.test(value) || 'El campo recibe solo valores numéricos.';
  },

  numericoFormateado: value => {
    if (value === null || value === '') return true;
    const sinFormato = String(value)
      .replaceAll('.', '')
      .replace(',', '.');
    return (
      /^\d+(\.\d+)?$/.test(sinFormato) ||
      'El campo recibe solo valores numéricos.'
    );
  },

  telefonoCaracter: value => {
    return (
      (value && value.length >= 7 && value.length <= 10) ||
      'El número telefónico debe contener de 7 a 10 caracteres.'
    );
  },

  passwordCaracter: value => {
    return (
      (value && value.length >= 8 && value.length <= 64) ||
      'La contraseña debe contener de 8 a 64 caracteres.'
    );
  },

  soloLetrasYEspacios: value => {
    const pattern = /^[A-Za-zñÑ\sÁ-Úá-ú]+$/;
    return (
      !value ||
      (pattern.test(value) && value.length <= 50) ||
      'El campo solo puede contener letras y espacios y no debe exceder los 50 caracteres.'
    );
  },
  alfanumerico: tipo => {
    const regexMap = {
      default: /^[A-Za-z0-9\s!@#$%&*()_+\-=[\]{};':"\\|,.<>/?]+$/,
      sinEspacios: /^[A-Za-z0-9!@#$%&*()_+\-=[\]{};':"\\|,.<>/?]+$/,
      sinEspeciales: /^[A-Za-z0-9\s]+$/,
    };

    const mensajes = {
      default: 'El campo no debe contener caracteres inválidos',
      sinEspacios: 'El campo no debe contener espacios',
      sinEspeciales: 'El campo no debe contener caracteres especiales',
    };

    const key = tipo?.trim();
    if (regexMap[key]) {
      return value => !value || regexMap[key].test(value) || mensajes[key];
    }

    return !tipo || regexMap.default.test(tipo) || mensajes.default;
  },

  urlCaracter: value => {
    const texto = String(value ?? '');
    return texto.length <= 100 || 'La URL no debe exceder los 100 caracteres';
  },

  urlValida: value => {
    const texto = String(value ?? '').trim();
    if (!texto) return true;

    const pattern =
      /^https?:\/\/([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*|localhost|(\d{1,3}\.){3}\d{1,3})(:\d+)?(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)*/;
    return pattern.test(texto) || 'El campo url debe ser una URL válida';
  },
};

/**
 * Obtiene las reglas de validación basadas en los nombres, definiciones con parámetros o funciones proporcionadas.
 *
 * Admite los siguientes formatos:
 *
 * 1. **Nombre de regla**: corresponde a una clave definida en el objeto `rules`.
 *    Ejemplo: `"required"`.
 *
 * 2. **Nombre de regla con parámetros**: usando el formato `"nombre:param"`.
 *    - Para reglas numéricas o de longitud, el parámetro se convierte a número.
 *      Ejemplo: `"exactLength:9"`, `"maxLength:10"`.
 *
 * @param {...(string|Function)} validations - Lista de validaciones a aplicar.
 * @returns {Function[]} Array de funciones de validación correspondientes a las validaciones proporcionadas.
 *
 * @example
 * getRules('required', 'exactLength:9')
 *
 */
export const getRules = (...validations) => {
  const reglasRecibidas = validations.map(validation => {
    if (typeof validation === 'string') {
      if (validation.includes(':')) {
        const [name, param] = validation.split(':');
        const reglaFactory = rules[name];
        if (typeof reglaFactory === 'function') {
          const num = Number(param);
          return Number.isNaN(num) ? reglaFactory(param) : reglaFactory(num);
        }
        console.error(`La regla "${name}" no está definida`);
        return null;
      }

      const regla = rules[validation];
      if (regla) return regla;
      console.error(`La regla "${validation}" no está definida`);
      return null;
    }

    if (typeof validation === 'function') return validation;

    console.error(`Tipo de validación no soportado: ${validation}`);
    return null;
  });

  return reglasRecibidas.filter(Boolean);
};
